<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('users.usersLabel') }}</span>
        <h3 class="page-title">{{ $t('users.usersList') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
          @click.prevent="$router.push('/uzytkownicy/nowy')">
          <i class="material-icons">add</i>
          {{ $t('users.addUser') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded && currentUserID > 0"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="users-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('users.usersSearchPlaceholder')"
          v-model="filters.search" />

        <v-select
          style="width: 360px;"
          class="ml-2"
          :options="accountActiveOptions"
          v-model="filters.accountActive"
          :searchable="false"
          :placeholder="$t('users.isAccountActive')"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.nameAndSurename') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.emailAddress') }}</th>
            <th scope="col" class="border-0">{{ $t('users.isAccountActive') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded && currentUserID">
          <tr
            v-for="(user, index) of users"
            :key="'user-' + index">
            <td class="align-middle">
              {{ user.name }}
            </td>

            <td class="align-middle">
              {{ user.email }}
            </td>

            <td class="align-middle">
              <span
                v-if="user.active === 1"
                class="text-success">
                {{ $t('users.active') }}
              </span>
              <span
                v-if="user.active === 0"
                class="text-danger">
                {{ $t('users.inactive') }}
              </span>
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/uzytkownicy/edycja/' + user.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="user.id === currentUserID || removeInProgress"
                @click.native="removeUser(user.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="users.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('users.noUsersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded || !currentUserID" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'users',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    accountActiveOptions () {
      return [
        {
          text: this.$t('users.accountActive'),
          value: 1
        },
        {
          text: this.$t('users.accountInactive'),
          value: 0
        }
      ];
    }
  },
  data() {
    return {
      users: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      currentUserID: 0,
      filters: {
        search: '',
        accountActive: ''
      }
    };
  },
  mounted () {
    this.currentUserID = this.$store.getters.getUserID;
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.accountActive !== null && this.filters.accountActive && typeof this.filters.accountActive.value === 'number') {
        where.active = this.filters.accountActive.value;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/customer-panel/users/items?page=' + this.currentPage,
        listField: 'users',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        accountActive: ''
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['users-filters-form'], this.debouncedLoadFilteredData);
    },
    removeUser (userID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customer-panel/users/delete/',
        id: userID,
        successTitle: this.$t('users.deleteSuccessTitle'),
        successText: this.$t('users.deleteSuccessText'),
        confirmTitle: this.$t('users.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('users.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>
